// ---------------------------------------------------------------------------
// YouAreYou (uru) — ActivityPage
//
// Full activity history log, linked from the dashboard's "View All" button.
// Shows all connection events (created, verified, denied, cancelled) in
// reverse chronological order with filtering, search, and pagination.
//
// Route: /activity
//
// API references (docs/api.md):
//   - GET /api/uru/connections → all connections with filters, pagination
//
// Design notes:
//   No dedicated mockup exists. Follows the design system established in
//   the dashboard (DashboardPage) and connections list (ConnectionsPage):
//   same card styles, status badges, relative timestamps.
//
//   Desktop: centered content column with filter bar + activity list
//   Mobile: full-width list, sticky filter bar, bottom tab nav clearance
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Icon from "@/components/ui/Icon";
import { listConnections, getConnection, cancelConnection } from "@/api/endpoints";
import { ApiRequestError, NetworkError } from "@/api/client";
import type { Connection, ConnectionStatus } from "@/types/api";
import { useAuth } from "@/hooks/useAuth";
import {
  formatRelativeTime,
  formatDateTime,
  statusColorClass,
  statusLabel,
  statusIcon,
  copyToClipboard,
  cn,
} from "@/lib/utils";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PAGE_SIZE = 20;

type FilterStatus = "all" | ConnectionStatus;

interface FilterOption {
  key: FilterStatus;
  label: string;
  icon: string;
}

const FILTER_OPTIONS: FilterOption[] = [
  { key: "all", label: "All", icon: "list" },
  { key: "verified", label: "Verified", icon: "check_circle" },
  { key: "pending", label: "Pending", icon: "schedule" },
  { key: "denied", label: "Denied", icon: "cancel" },
  { key: "cancelled", label: "Cancelled", icon: "block" },
];

const CARD_SHADOW = "shadow-[0px_4px_20px_rgba(26,31,43,0.04)]";

const STATUS_PILL: Record<string, string> = {
  verified: "bg-[#e8f3ec] text-[#3f7d54]",
  pending: "bg-[#fff4e0] text-[#92671e]",
  denied: "bg-[#ffe6e3] text-[#ba1a1a]",
  cancelled: "bg-[#f1f3ff] text-[#877271]",
};

// ---------------------------------------------------------------------------
// CopyLinkButton — inline copy button with a brief "Copied!" flash
// ---------------------------------------------------------------------------

function CopyLinkButton({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    const ok = await copyToClipboard(url);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [url]);

  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); void handleCopy(); }}
      className={cn(
        "flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500/20",
        copied
          ? "bg-emerald-50 text-emerald-600"
          : "bg-gray-100 text-text-secondary hover:bg-primary-50 hover:text-primary-600",
      )}
      aria-label={copied ? "Copied" : "Copy link"}
    >
      <Icon name={copied ? "check" : "content_copy"} className="text-sm" />
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

// ---------------------------------------------------------------------------
// ActivityIcon — icon for each activity event type
// ---------------------------------------------------------------------------

function ActivityIcon({ connection }: { connection: Connection }) {
  const isDirectLink = connection.source === "direct_link";

  if (connection.status === "verified") {
    return (
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-green-50">
        <Icon name="check_circle" filled className="text-xl text-verified" />
      </div>
    );
  }

  if (connection.status === "denied") {
    return (
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-50">
        <Icon name="cancel" filled className="text-xl text-error" />
      </div>
    );
  }

  if (connection.status === "cancelled") {
    return (
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-100">
        <Icon name="block" className="text-xl text-text-muted" />
      </div>
    );
  }

  // pending
  return (
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-50">
      <Icon
        name={isDirectLink ? "add_link" : "person_add"}
        className="text-xl text-warning"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// ActivityEventTitle — descriptive title for each event
// ---------------------------------------------------------------------------

function getEventTitle(connection: Connection): string {
  const isDirectLink = connection.source === "direct_link";

  switch (connection.status) {
    case "verified":
      return isDirectLink
        ? "Direct link connection verified"
        : "Global link connection verified";
    case "pending":
      return isDirectLink
        ? "Direct trust link created"
        : "Connection request sent";
    case "denied":
      return "Connection request declined";
    case "cancelled":
      return "Connection request cancelled";
    default:
      return "Connection event";
  }
}

function getEventSubtitle(connection: Connection): string {
  const otherName = connection.connected_customer
    ? `${connection.connected_customer.first_name} ${connection.connected_customer.last_name}`.trim()
    : "Pending recipient";
  const directionLabel = connection.direction === "outgoing" ? "You" : otherName;

  switch (connection.status) {
    case "verified":
      return `Connected with ${otherName}`;
    case "pending":
      return connection.direction === "outgoing"
        ? `Sent to ${otherName}`
        : `From ${otherName}`;
    case "denied":
      return connection.denial_reason
        ? `Reason: ${connection.denial_reason}`
        : `Request declined`;
    case "cancelled":
      return `Cancelled`;
    default:
      return `${directionLabel} → ${otherName}`;
  }
}

// ---------------------------------------------------------------------------
// ActivityItem — single event row
// ---------------------------------------------------------------------------

function ActivityItem({
  connection,
  onCancel,
  userFirstName,
  anonIndex,
}: {
  connection: Connection;
  onCancel?: () => Promise<void>;
  userFirstName?: string;
  anonIndex?: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const handleCancel = useCallback(async () => {
    if (!onCancel || cancelling) return;
    setCancelling(true);
    try {
      await onCancel();
    } finally {
      setCancelling(false);
    }
  }, [onCancel, cancelling]);

  const shortLink = connection.link
    ? connection.link.replace(/^https?:\/\//, "")
    : null;

  return (
    <div className={`rounded-2xl bg-white transition-shadow hover:shadow-[0px_8px_32px_rgba(26,31,43,0.08)] ${CARD_SHADOW}`}>
      {/* Main row */}
      <button
        onClick={() => setExpanded((prev) => !prev)}
        className="flex w-full items-center gap-3 px-4 py-3.5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/20"
        aria-expanded={expanded}
      >
        {/* Icon */}
        <ActivityIcon connection={connection} />

        {/* Content */}
        <div className="min-w-0 flex-1">
          {connection.source === "direct_link" ? (
            // Direct links: show the label/recipient as primary text (matches dashboard style)
            <p className="text-sm font-medium text-text-primary">
              {connection.label ||
                (connection.connected_customer
                  ? `${connection.connected_customer.first_name} ${connection.connected_customer.last_name}`.trim()
                  : `${userFirstName ?? "My"}'s Trust link ${String(anonIndex ?? 1).padStart(3, "0")}`)}
            </p>
          ) : (
            <>
              <p className="text-sm font-medium text-text-primary">
                {getEventTitle(connection)}
              </p>
              <p className="mt-0.5 truncate text-xs text-text-muted">
                {getEventSubtitle(connection)}
              </p>
            </>
          )}
        </div>

        {/* Right side: time + status + chevron */}
        <div className="flex shrink-0 items-center gap-2">
          <div className="text-right">
            <p className="text-xs text-text-secondary">
              {formatRelativeTime(connection.created)}
            </p>
            <span
              className={cn(
                "rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide",
                STATUS_PILL[connection.status] ?? "bg-[#f1f3ff] text-[#877271]",
              )}
            >
              {statusLabel(connection.status)}
            </span>
          </div>
          <Icon
            name={expanded ? "expand_less" : "expand_more"}
            className="text-xl text-text-muted"
          />
        </div>
      </button>

      {/* Link container — shown inline for direct_link connections that have a URL */}
      {shortLink && (
        <div className="mx-4 mb-3 flex items-center gap-3 rounded-lg border border-gray-100 bg-gray-50 px-4 py-2.5">
          <span className="flex-1 truncate text-center text-sm font-medium text-text-secondary">
            {shortLink}
          </span>
          <CopyLinkButton url={connection.link!} />
        </div>
      )}

      {/* Expanded detail */}
      {expanded && (
        <div className="border-t border-gray-100 px-4 py-3">
          <div className="grid grid-cols-2 gap-3 text-xs sm:grid-cols-3">
            {/* Status */}
            <div>
              <p className="font-medium text-text-muted uppercase tracking-wider">
                Status
              </p>
              <p className="mt-0.5 flex items-center gap-1">
                <Icon
                  name={statusIcon(connection.status)}
                  filled
                  className={cn("text-sm", statusColorClass(connection.status))}
                />
                <span
                  className={cn(
                    "font-medium",
                    statusColorClass(connection.status),
                  )}
                >
                  {statusLabel(connection.status)}
                </span>
              </p>
            </div>

            {/* Created */}
            <div>
              <p className="font-medium text-text-muted uppercase tracking-wider">
                Created
              </p>
              <p className="mt-0.5 text-text-primary">
                {formatDateTime(connection.created)}
              </p>
            </div>

            {/* Modified */}
            {connection.modified && (
              <div>
                <p className="font-medium text-text-muted uppercase tracking-wider">
                  Last Updated
                </p>
                <p className="mt-0.5 text-text-primary">
                  {formatDateTime(connection.modified)}
                </p>
              </div>
            )}
          </div>

          {/* Contact + Denial Reason */}
          <div className="mt-3 flex flex-wrap gap-4 border-t border-gray-100 pt-3 text-xs">
            <div>
              <p className="font-medium text-text-muted uppercase tracking-wider">
                Contact
              </p>
              <p className="mt-0.5 text-text-primary">
                {connection.connected_customer
                  ? `${connection.connected_customer.first_name} ${connection.connected_customer.last_name}`
                  : "Pending recipient"}
              </p>
            </div>
            {connection.denial_reason && (
              <div>
                <p className="font-medium text-text-muted uppercase tracking-wider">
                  Denial Reason
                </p>
                <p className="mt-0.5 text-text-primary">
                  {connection.denial_reason}
                </p>
              </div>
            )}
          </div>

          {/* Cancel button — pending outbound connections only */}
          {connection.status === "pending" && connection.direction === "outgoing" && onCancel && (
            <div className="mt-3 border-t border-gray-100 pt-3">
              <button
                onClick={() => { void handleCancel(); }}
                disabled={cancelling}
                className="flex w-full items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-text-secondary transition-colors hover:border-error/30 hover:bg-red-50 hover:text-error disabled:cursor-not-allowed disabled:opacity-50"
              >
                {cancelling ? (
                  <Icon name="progress_activity" className="animate-spin text-lg" />
                ) : (
                  <Icon name="block" className="text-lg" />
                )}
                Cancel Request
              </button>
            </div>
          )}

          {/* View full details */}
          <div className="mt-3 flex justify-center">
            <Link
              to={`/connections/${connection.id}`}
              className="inline-flex items-center gap-1 text-xs font-medium text-primary-500 hover:text-primary-600 transition-colors"
            >
              View full details
              <Icon name="arrow_forward" className="text-sm" />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Loading Skeleton
// ---------------------------------------------------------------------------

function LoadingSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="h-[72px] animate-pulse rounded-xl border border-gray-200 bg-gray-100"
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty State
// ---------------------------------------------------------------------------

function EmptyState({
  filter,
  hasSearch,
}: {
  filter: FilterStatus;
  hasSearch: boolean;
}) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
        <Icon name="history" className="text-3xl text-text-muted" />
      </div>
      {hasSearch ? (
        <>
          <h3 className="text-base font-semibold text-text-primary">
            No matching activity
          </h3>
          <p className="mt-1 text-sm text-text-secondary">
            Try adjusting your search or filter.
          </p>
        </>
      ) : filter !== "all" ? (
        <>
          <h3 className="text-base font-semibold text-text-primary">
            No {filter} connections
          </h3>
          <p className="mt-1 max-w-xs text-sm text-text-secondary">
            You don&apos;t have any {filter} connections yet.
          </p>
        </>
      ) : (
        <>
          <h3 className="text-base font-semibold text-text-primary">
            No activity yet
          </h3>
          <p className="mt-1 max-w-xs text-sm text-text-secondary">
            Share your trust link to start building your verified network.
            Activity will appear here as connections are made.
          </p>
          <Link
            to="/dashboard"
            className="mt-4 inline-flex items-center gap-1 rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
          >
            Go to Dashboard
            <Icon name="arrow_forward" className="text-lg" />
          </Link>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Error State
// ---------------------------------------------------------------------------

function ErrorState({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-50">
        <Icon name="error" className="text-3xl text-error" />
      </div>
      <h3 className="text-base font-semibold text-text-primary">
        Failed to load activity
      </h3>
      <p className="mt-1 max-w-xs text-sm text-text-secondary">{message}</p>
      <button
        onClick={onRetry}
        className="mt-4 rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600 focus:ring-2 focus:ring-primary-500/20 focus:outline-none"
      >
        Try again
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// StatusFilterBar — horizontal filter pills
// ---------------------------------------------------------------------------

function StatusFilterBar({
  active,
  onChange,
  counts,
}: {
  active: FilterStatus;
  onChange: (status: FilterStatus) => void;
  counts: Record<FilterStatus, number>;
}) {
  return (
    <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-hidden">
      {FILTER_OPTIONS.map((opt) => {
        const isActive = active === opt.key;
        const count = counts[opt.key];

        return (
          <button
            key={opt.key}
            onClick={() => onChange(opt.key)}
            className={cn(
              "inline-flex shrink-0 items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors",
              isActive
                ? "bg-primary-500 text-white shadow-sm"
                : "bg-gray-100 text-text-secondary hover:bg-gray-200",
            )}
          >
            <Icon name={opt.icon} className="text-sm" />
            {opt.label}
            {count > 0 && (
              <span
                className={cn(
                  "rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-none",
                  isActive
                    ? "bg-white/20 text-white"
                    : "bg-gray-200 text-text-muted",
                )}
              >
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SearchBar
// ---------------------------------------------------------------------------

function SearchBar({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="relative">
      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
        <Icon name="search" className="text-lg text-text-muted" />
      </div>
      <input
        type="search"
        placeholder="Search by name…"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full rounded-xl bg-white py-2.5 pl-10 pr-4 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-4 focus:ring-primary-500/15 ${CARD_SHADOW}`}
        aria-label="Search activity"
      />
      {value && (
        <button
          onClick={() => onChange("")}
          className="absolute inset-y-0 right-0 flex items-center pr-3 text-text-muted hover:text-text-secondary"
          aria-label="Clear search"
        >
          <Icon name="close" className="text-lg" />
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Summary Stats — quick counts at the top
// ---------------------------------------------------------------------------

function SummaryStats({ counts }: { counts: Record<FilterStatus, number> }) {
  const stats = [
    {
      label: "Total",
      value: counts.all,
      icon: "bar_chart",
      color: "text-primary-500",
      bg: "bg-primary-50",
    },
    {
      label: "Verified",
      value: counts.verified,
      icon: "check_circle",
      color: "text-verified",
      bg: "bg-green-50",
    },
    {
      label: "Pending",
      value: counts.pending,
      icon: "schedule",
      color: "text-warning",
      bg: "bg-amber-50",
    },
    {
      label: "Denied",
      value: counts.denied,
      icon: "cancel",
      color: "text-error",
      bg: "bg-red-50",
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
      {stats.map((stat) => (
        <div key={stat.label} className={`rounded-2xl bg-white px-5 py-4 ${CARD_SHADOW}`}>
          <p className="text-sm font-medium text-[#544242]">{stat.label}</p>
          <p className="mt-1 flex items-center gap-1.5 text-3xl font-extrabold text-[#161b27]">
            {stat.value.toLocaleString()}
            <Icon name={stat.icon} filled className={cn("text-base", stat.color)} />
          </p>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ActivityPage — Main Component
// ---------------------------------------------------------------------------

export default function ActivityPage() {
  const { user } = useAuth();
  const userFirstName = user?.first_name || "My";

  // ---- State ----
  const [connections, setConnections] = useState<Connection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterStatus>("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);

  // ---- Fetch all connections ----

  const fetchConnections = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await listConnections({ size: 500, sort: "-created" });

      // Read the dashboard's localStorage link cache to fill in link URLs without
      // extra API calls where possible.
      let linkCache: Connection[] = [];
      try {
        const stored = localStorage.getItem("uru_created_links");
        if (stored) linkCache = JSON.parse(stored) as Connection[];
      } catch { /* ignore */ }

      // Merge cache, then fetch detail for any direct_link still missing a link URL.
      const merged = data.map((conn) =>
        linkCache.find((c) => c.id === conn.id) ?? conn,
      );

      const enriched = await Promise.all(
        merged.map((conn) =>
          conn.source === "direct_link" && !conn.link
            ? getConnection(conn.id).catch(() => conn)
            : Promise.resolve(conn),
        ),
      );

      setConnections(enriched);
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setError(err.message);
      } else if (err instanceof NetworkError) {
        setError("Unable to connect. Please check your internet connection.");
      } else {
        setError("Failed to load activity. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);

  // ---- Cancel a pending outbound connection ----

  const handleCancel = useCallback(async (id: number) => {
    await cancelConnection(id);
    // Update status in-place so the item reflects the new state immediately
    setConnections((prev) =>
      prev.map((c) =>
        c.id === id ? { ...c, status: "cancelled" as ConnectionStatus } : c,
      ),
    );
  }, []);

  // ---- Status counts ----

  const counts = useMemo(() => {
    const result: Record<FilterStatus, number> = {
      all: connections.length,
      verified: 0,
      pending: 0,
      denied: 0,
      cancelled: 0,
    };

    for (const conn of connections) {
      if (conn.status in result) {
        result[conn.status as ConnectionStatus]++;
      }
    }

    return result;
  }, [connections]);

  // ---- Filter by status ----

  const statusFiltered = useMemo(() => {
    if (filter === "all") return connections;
    return connections.filter((c) => c.status === filter);
  }, [connections, filter]);

  // ---- Filter by search (names) ----

  const searchFiltered = useMemo(() => {
    if (!search.trim()) return statusFiltered;
    const q = search.toLowerCase().trim();
    return statusFiltered.filter((c) => {
      const contactName = c.connected_customer
        ? `${c.connected_customer.first_name} ${c.connected_customer.last_name}`.toLowerCase()
        : "";
      return contactName.includes(q);
    });
  }, [statusFiltered, search]);

  // ---- Paginate ----

  const totalPages = Math.max(1, Math.ceil(searchFiltered.length / PAGE_SIZE));
  const paginatedConnections = useMemo(
    () => searchFiltered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE),
    [searchFiltered, page],
  );

  // Reset page when filter or search changes
  useEffect(() => {
    setPage(0);
  }, [filter, search]);

  // ---- Render ----

  return (
    <div className="mx-auto w-full max-w-4xl px-4 py-6 sm:px-6 md:py-10">
      {/* ---- Header ---- */}
      <div className="mb-6">
        <h1 className="text-2xl font-extrabold tracking-tight text-[#161b27] sm:text-3xl">Activity History</h1>
        <p className="mt-1 text-sm text-[#544242]">
          Detailed logs of all your identity verifications and document interactions.
        </p>
      </div>

      {/* ---- Summary Stats ---- */}
      {!loading && !error && connections.length > 0 && (
        <div className="mb-6">
          <SummaryStats counts={counts} />
        </div>
      )}

      {/* ---- Search + Filter Bar ---- */}
      {!loading && !error && connections.length > 0 && (
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="flex-1">
            <SearchBar value={search} onChange={setSearch} />
          </div>
          <div className="flex-shrink-0">
            <StatusFilterBar
              active={filter}
              onChange={setFilter}
              counts={counts}
            />
          </div>
        </div>
      )}

      {/* ---- Results count ---- */}
      {!loading && !error && connections.length > 0 && (
        <div className="mb-3 flex items-center justify-between text-xs text-text-muted">
          <span>
            Showing {paginatedConnections.length} of {searchFiltered.length}{" "}
            {filter !== "all" ? `${filter} ` : ""}
            event{searchFiltered.length !== 1 ? "s" : ""}
          </span>
          {totalPages > 1 && (
            <span>
              Page {page + 1} of {totalPages}
            </span>
          )}
        </div>
      )}

      {/* ---- Content ---- */}
      {loading && <LoadingSkeleton />}

      {error && !loading && (
        <ErrorState message={error} onRetry={fetchConnections} />
      )}

      {!loading && !error && searchFiltered.length === 0 && (
        <EmptyState filter={filter} hasSearch={!!search.trim()} />
      )}

      {!loading && !error && paginatedConnections.length > 0 && (() => {
        const anonIndexMap = new Map<number, number>();
        [...connections]
          .filter((c) => !c.label && !c.connected_customer)
          .sort((a, b) => a.created - b.created)
          .forEach((c, i) => anonIndexMap.set(c.id, i + 1));

        return (
          <div className="flex flex-col gap-3 pb-24 md:pb-8">
            {paginatedConnections.map((conn) => (
              <ActivityItem
                key={conn.id}
                connection={conn}
                userFirstName={userFirstName}
                anonIndex={anonIndexMap.get(conn.id)}
                onCancel={
                  conn.status === "pending" && conn.direction === "outgoing"
                    ? () => handleCancel(conn.id)
                    : undefined
                }
              />
            ))}
          </div>
        );
      })()}

      {/* ---- Pagination ---- */}
      {!loading && !error && totalPages > 1 && (
        <div className="mt-4 flex items-center justify-center gap-2 pb-24 md:pb-8">
          <button
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-text-secondary transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Icon name="chevron_left" className="text-lg" />
            Previous
          </button>

          {/* Page numbers — show up to 5 pages around current */}
          <div className="hidden items-center gap-1 sm:flex">
            {Array.from({ length: totalPages }, (_, i) => i)
              .filter(
                (i) =>
                  i === 0 ||
                  i === totalPages - 1 ||
                  Math.abs(i - page) <= 1,
              )
              .reduce<(number | "ellipsis")[]>((acc, i, idx, arr) => {
                if (idx > 0 && arr[idx - 1] !== i - 1) {
                  acc.push("ellipsis");
                }
                acc.push(i);
                return acc;
              }, [])
              .map((item, idx) =>
                item === "ellipsis" ? (
                  <span
                    key={`ellipsis-${idx}`}
                    className="px-1 text-text-muted"
                  >
                    …
                  </span>
                ) : (
                  <button
                    key={item}
                    onClick={() => setPage(item)}
                    className={cn(
                      "flex h-8 w-8 items-center justify-center rounded-lg text-sm font-medium transition-colors",
                      page === item
                        ? "bg-primary-500 text-white"
                        : "text-text-secondary hover:bg-gray-100",
                    )}
                  >
                    {item + 1}
                  </button>
                ),
              )}
          </div>

          {/* Mobile: simple "Page X of Y" */}
          <span className="text-sm text-text-muted sm:hidden">
            {page + 1} / {totalPages}
          </span>

          <button
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
            className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-text-secondary transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Next
            <Icon name="chevron_right" className="text-lg" />
          </button>
        </div>
      )}
    </div>
  );
}
